import { useQuery } from '@tanstack/react-query';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { GetProductReportingStatusResponse } from ':cloud/types/service_proto';
import { ProductTypeCOMS, Project } from ':cloud/types/ts_types';

async function getProductReportingStatus(
  organizationId: string,
  projectId: string,
  productType: string,
  runId: string,
): Promise<GetProductReportingStatusResponse> {
  const path = `/organization-management/v1/organizations/${organizationId}/projects/${projectId}/products:getProductReportingStatus?productType=${productType}&runId=${runId}`;
  const { data } = await APIClientBase.get<GetProductReportingStatusResponse>(path);
  return data;
}

export function useGetProductReportingStatus(
  project: Project,
  productType: ProductTypeCOMS,
  runId: string,
): { isLoading: boolean; data?: GetProductReportingStatusResponse } {
  const toast = useToast();
  const { isLoading, data } = useQuery({
    queryKey: [QueryKey.productDetails, project.organizationId, project.id, productType, runId],
    queryFn: async () => {
      const response = await getProductReportingStatus(
        project.organizationId,
        project.id,
        productType,
        runId,
      );
      return response;
    },
    staleTime: Infinity,
    enabled: !!project && !!productType && runId.trim().length > 0,
    refetchInterval: (resp) => {
      if (resp?.status === 'PRODUCT_REPORTING_STATUS_PENDING') {
        return 1500;
      }
      return false;
    },
    onError: () => {
      toast.show('Unable to generate report. Please shorten the date range or try again later.', {
        variant: 'negative',
      });
    },
    onSuccess: (resp) => {
      if (resp?.status === 'PRODUCT_REPORTING_STATUS_COMPLETED') {
        toast.show('Report generation successful', {
          variant: 'positive',
        });
      } else if (resp?.status === 'PRODUCT_REPORTING_STATUS_FAILED') {
        toast.show('Unable to generate report. Please shorten the date range or try again later.', {
          variant: 'negative',
        });
      }
    },
  });

  return {
    isLoading,
    data,
  };
}
